/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { Quill } from 'react-quill'
import { flattenToInline } from './pasteHtml'

// BlockEmbed for tables — uses contenteditable + event isolation so all cells are
// fully editable while Quill preserves the HTML across re-renders.
const BlockEmbed = Quill.import('blots/block/embed')

class TableEmbed extends BlockEmbed {
  static blotName = 'tableEmbed'
  static tagName = 'DIV'
  static className = 'ql-table-embed'

  static create(value: string) {
    const node = super.create() as HTMLElement
    node.innerHTML = value
    node.setAttribute('contenteditable', 'false')

    node.querySelectorAll('td, th').forEach((cell) => {
      cell.setAttribute('contenteditable', 'true')
    })

    const stopQuill = (e: Event) => e.stopPropagation()
    node.addEventListener('copy', stopQuill, true)
    node.addEventListener('cut', stopQuill, true)

    node.addEventListener('paste', (e: Event) => {
      e.stopPropagation()
      const ce = e as ClipboardEvent
      const html = ce.clipboardData?.getData('text/html')
      const text = ce.clipboardData?.getData('text/plain')
      if (html) {
        ce.preventDefault()
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = html
        const body = tempDiv.querySelector('body')
        tempDiv.innerHTML = body ? body.innerHTML : tempDiv.innerHTML
        document.execCommand('insertHTML', false, flattenToInline(tempDiv))
      } else if (text) {
        ce.preventDefault()
        document.execCommand('insertText', false, text)
      }
    }, true)

    // Put the caret in the block just before/after the table, creating one
    // when the table sits at that end of the document. Cells are their own
    // contenteditable islands inside a contenteditable=false blot, so Quill's
    // selection still points at wherever the caret was before the table was
    // entered — stepping out has to be done through the document index, not
    // by letting the keystroke fall through to Quill.
    const escapeTable = (after: boolean): boolean => {
      const container = node.closest('.ql-container') as HTMLElement | null
      const quill: any = container ? (Quill as any).find(container) : null
      const blot: any = (Quill as any).find(node)
      if (!quill || !blot) return false
      // A block embed is one character long, so the block after it starts at
      // index + 1 and the one before it ends at index.
      const index = quill.getIndex(blot)
      const target = after ? index + 1 : index
      if (after ? !blot.next : !blot.prev) quill.insertText(target, '\n', 'user')
      quill.setSelection(target, 0, 'user')
      return true
    }

    node.addEventListener('keydown', (e: Event) => {
      const ke = e as KeyboardEvent
      if (ke.key !== 'Tab' && ke.key !== 'Enter') return
      const target = ke.target as HTMLElement | null
      const cell = target?.closest?.('td, th') as HTMLTableCellElement | null
      if (!cell || !node.contains(cell)) return

      const cells = Array.from(node.querySelectorAll('td, th')) as HTMLTableCellElement[]
      const idx = cells.indexOf(cell)
      if (idx === -1) return

      // Quill must never see a keystroke aimed at a cell — its Tab/Enter
      // bindings act on that stale root selection and edit the text around
      // the table instead. Same isolation the copy/cut/paste handlers use.
      ke.stopPropagation()

      if (ke.key === 'Enter') {
        // From the last cell, Enter leaves the table. Anywhere else the
        // native line break stands, so cells can still hold several lines.
        if (idx === cells.length - 1 && escapeTable(true)) ke.preventDefault()
        return
      }

      const nextIdx = ke.shiftKey ? idx - 1 : idx + 1
      if (nextIdx < 0 || nextIdx >= cells.length) {
        // Tabbed off the end (or Shift+Tab off the front) — step out of the
        // table on that side.
        if (escapeTable(!ke.shiftKey)) ke.preventDefault()
        return
      }

      ke.preventDefault()

      const nextCell = cells[nextIdx]
      nextCell.focus()
      const selection = window.getSelection()
      if (selection) {
        const range = document.createRange()
        range.selectNodeContents(nextCell)
        // Select existing text so it can be typed over; empty cells just get a caret.
        if (!nextCell.textContent?.trim()) range.collapse(false)
        selection.removeAllRanges()
        selection.addRange(range)
      }
    }, true)

    const deleteBtn = document.createElement('button')
    deleteBtn.className = 'ql-table-delete-btn'
    deleteBtn.setAttribute('contenteditable', 'false')
    deleteBtn.setAttribute('type', 'button')
    deleteBtn.innerHTML = '×'
    deleteBtn.title = 'Remove table'
    deleteBtn.addEventListener('mousedown', (e) => {
      e.preventDefault()
      e.stopPropagation()
      node.remove()
    })
    node.appendChild(deleteBtn)

    const editBtn = document.createElement('button')
    editBtn.className = 'ql-table-edit-btn'
    editBtn.setAttribute('contenteditable', 'false')
    editBtn.setAttribute('type', 'button')
    editBtn.innerHTML = '✎'
    editBtn.title = 'Edit table'
    editBtn.addEventListener('mousedown', (e) => {
      e.preventDefault()
      e.stopPropagation()
      document.querySelectorAll('.ql-table-embed.ql-table-edit-mode').forEach((el) => {
        if (el !== node) el.classList.remove('ql-table-edit-mode')
      })
      node.classList.toggle('ql-table-edit-mode')
      window.dispatchEvent(new CustomEvent('ql-table-edit-change'))
    })
    node.appendChild(editBtn)

    return node
  }

  static value(node: HTMLElement) {
    const clone = node.cloneNode(true) as HTMLElement
    clone.querySelector('.ql-table-delete-btn')?.remove()
    clone.querySelector('.ql-table-edit-btn')?.remove()
    // Don't persist per-cell editability. create() re-adds it for live editors;
    // leaving it in the saved HTML makes cells editable in read-only views
    // (posted comments render the raw HTML directly, no blot rebuild).
    clone.querySelectorAll('td, th').forEach((cell) => cell.removeAttribute('contenteditable'))
    return clone.innerHTML
  }

  // Block Quill from programmatically deleting the table via normal edits.
  deleteAt() {
    return
  }

  length() {
    return 1
  }
}

Quill.register(TableEmbed, true)

export { BlockEmbed }
export default TableEmbed
